import React, { useState, useRef } from 'react'
import { Plus } from 'lucide-react'
import AddFieldMenu from './AddFieldMenu'

interface AddFieldButtonProps {
  onAddField: (fieldType: string) => void
  position?: 'between' | 'end'
}

/**
 * Plus button that appears between fields (or at end)
 * Opens AddFieldMenu when clicked
 *
 * Pattern from email builder: DividerButton + BlocksMenu
 */
export default function AddFieldButton({ onAddField, position = 'between' }: AddFieldButtonProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(true)
  }

  const handleSelectField = (fieldType: string) => {
    onAddField(fieldType)
    setMenuOpen(false)
  }

  if (position === 'between') {
    // Divider style button between fields
    return (
      <>
        <div
          ref={buttonRef}
          className="group relative h-8 flex items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors"
          onClick={handleClick}
        >
          {/* Divider line */}
          <div className="absolute inset-x-0 h-px bg-border group-hover:bg-blue-300 transition-colors" />

          {/* Plus button */}
          <div className="relative bg-white border-2 border-border group-hover:border-blue-500 group-hover:bg-blue-50 rounded-full p-1 transition-all">
            <Plus className="w-4 h-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
          </div>
        </div>

        <AddFieldMenu
          anchorEl={buttonRef.current}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onSelectField={handleSelectField}
        />
      </>
    )
  }

  // End position - just a button at the bottom
  return (
    <>
      <div
        ref={buttonRef}
        className="flex items-center justify-center py-4 border-2 border-dashed border-border hover:border-blue-500 hover:bg-blue-50 rounded-lg cursor-pointer transition-all group"
        onClick={handleClick}
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-blue-600 transition-colors">
          <Plus className="w-5 h-5" />
          <span>Add Field</span>
        </div>
      </div>

      <AddFieldMenu
        anchorEl={buttonRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSelectField={handleSelectField}
      />
    </>
  )
}
